import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Tabs,
  Tab,
  Card,
  Form,
  Button,
  ButtonGroup,
  Badge,
  InputGroup,
  CloseButton,
  Stack
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import "../styles/EditPage.css";

// --- Color helpers ---------------------------------------------------------
// Convert a hex string like "#3b82f6" or "3b82f6" to an RGB string "59, 130, 246"
const hexToRgbString = (hex) => {
  if (!hex) return '';
  let h = String(hex).trim().replace(/^#/, '');
  if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) return '';
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  const int = parseInt(h, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r}, ${g}, ${b}`;
};

// Convert an RGB string like "59, 130, 246" (with or without spaces) to hex like "#3b82f6"
const rgbStringToHex = (rgb) => {
  if (!rgb) return '#000000';
  const parts = String(rgb).split(',').map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '#000000';
  const [r, g, b] = parts.map((n) => Math.max(0, Math.min(255, n)));
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Normalize either an RGB string ("r, g, b") or hex ("#rrggbb") into an input-ready hex
const toColorInputValue = (value, fallback = '#3b82f6') => {
  if (!value) return fallback;
  const v = String(value).trim();
  if (v.includes(',')) return rgbStringToHex(v);
  // already hex-ish
  if (/^#?[0-9a-fA-F]{3}$/.test(v) || /^#?[0-9a-fA-F]{6}$/.test(v)) {
    return v.startsWith('#') ? v : `#${v}`;
  }
  return fallback;
};

// Ensure we save out "r, g, b" regardless of what's currently in state
const normalizeColorForSave = (value) => {
  if (!value) return value;
  const v = String(value).trim();
  if (v.includes(',')) return v; // already RGB string
  return hexToRgbString(v);       // convert hex -> RGB string
};
// ---------------------------------------------------------------------------

const EditPage = () => {
  const [rooms, setRooms] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`/api/rooms.json?ts=${Date.now()}`)
      .then(res => res.json())
      .then(data => setRooms(Array.isArray(data) ? data : []))
      .catch(() => setRooms([]));
  }, []);


  const handleReset = () => {
    fetch(`/api/rooms.json?ts=${Date.now()}`)
      .then(res => res.json())
      .then(data => setRooms(Array.isArray(data) ? data : []))
      .catch(() => setRooms([]));
  };



  const handleSave = async () => {
    const formData = new FormData();
    let hasPhotos = false;

    // Helper to pretty-print any response (string or object)
    const formatResponse = (payload) => {
      if (payload == null) return "— no body —";
      if (typeof payload === "string") return payload;
      try {
        return JSON.stringify(payload, null, 2);
      } catch {
        return String(payload);
      }
    };

    // Helper to extract error details from axios
    const extractError = (err) => {
      const status = err.response?.status;
      const statusText = err.response?.statusText;
      const body = formatResponse(err.response?.data);
      const msg = err.message;
      if (status) {
        return `HTTP ${status}${statusText ? ` ${statusText}` : ""}\n\nServer response:\n${body}`;
      }
      return `Error: ${msg}`;
    };

    rooms.forEach(room => {
      (room.images || []).forEach(image => {
        if (image.local) {
          formData.append('images[]', image.file);
          hasPhotos = true;
        }
      });
    });

    if (hasPhotos) {
      try {
        formData.append('username', username);
        formData.append('password', password);
        const { data } = await axios.post('/api/upload.php', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          auth: { username, password },
        });

        rooms.forEach(room => {
          (room.images || []).forEach(image => {
            if (image.local) {
              image.src = "/images/rooms/" + image.name;
              delete image.local;
              delete image.file;
            }
          });
        });
      } catch (err) {
        console.error('Error uploading files:', err);
        alert(`❌ Error uploading files.\n\n${extractError(err)}`);
        return; // stop if upload failed
      }
    } 

    // Before saving, make a copy and normalize any color fields to "r, g, b"
    const roomsForSave = rooms.map((r) => ({
      ...r,
      color: normalizeColorForSave(r.color) || '59, 130, 246',
    }));

    const jsonString = JSON.stringify(roomsForSave);

    try {
      const { data } = await axios.post(
        '/api/save.php',
        { json: jsonString, username, password },
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          auth: { username, password }
        }
      );

      alert(`✅ Data saved successfully.\n\nServer response:\n${formatResponse(data)}`);
      handleReset();
    } catch (err) {
      console.error('Error saving data:', err);
      alert(`❌ Error saving data.\n\n${extractError(err)}`);
      return;
    }

  };




  const handleTabSelect = (k) => {
    // Tabs gives eventKey back (we set it to the index)
    setActiveTabIndex(Number(k));
  };

  const handleAddRoom = () => {
    const newRoom = {
      id: Date.now(),
      title: 'New Room',
      description: '',
      capacity: '',
      bed: '',
      price: '',
      // store as RGB string to match the renderer's expectations
      color: '59, 130, 246', // corresponds to #3b82f6
      link: '',
      images: [],
      facilities: []
    };
    setRooms(prev => [...prev, newRoom]);
    setActiveTabIndex(rooms.length); // jump to the new room
  };

  const handleChange = (roomIndex, event, facilityIndex = null) => {
    const { name, type, value, files } = event.target;

    setRooms(prev => {
      const updated = [...prev];
      const updatedRoom = { ...updated[roomIndex] };

      if (type === 'file') {
        // Only used by the “Title images” input that calls handleAddImage
        // (kept here in case you wire file inputs directly to handleChange)
        updatedRoom[name] = Array.from(files).map(f => URL.createObjectURL(f));
      } else if (name === 'facilities' && facilityIndex !== null) {
        const nextFacilities = [...(updatedRoom.facilities || [])];
        nextFacilities[facilityIndex] = value;
        updatedRoom.facilities = nextFacilities;
      } else if (name === 'color') {
        // Convert the color input (hex) to the saved format ("r, g, b")
        updatedRoom.color = hexToRgbString(value) || updatedRoom.color;
      } else {
        updatedRoom[name] = value;
      }

      updated[roomIndex] = updatedRoom;
      return updated;
    });
  };

  const handleAddFacility = (roomIndex) => {
    setRooms(prev => {
      const updated = [...prev];
      const r = { ...updated[roomIndex] };
      r.facilities = [...(r.facilities || []), ''];
      updated[roomIndex] = r;
      return updated;
    });
  };

  const handleRemoveFacility = (roomIndex, facilityIndex) => {
    setRooms(prev => {
      const updated = [...prev];
      const r = { ...updated[roomIndex] };
      const facilities = [...(r.facilities || [])];
      facilities.splice(facilityIndex, 1);
      r.facilities = facilities;
      updated[roomIndex] = r;
      return updated;
    });
  };

  const handleAddImage = (roomIndex, event) => {
    const files = Array.from(event.target.files);
    const images = files.map(file => ({
      src: URL.createObjectURL(file),
      name: file.name,
      file: file,
      local: true
    }));
    setRooms(prev => {
      const updated = [...prev];
      const r = { ...updated[roomIndex] };
      r.images = [...(r.images || []), ...images];
      updated[roomIndex] = r;
      return updated;
    });
    // NOTE: In a production app, store File objects and revoke object URLs on unmount/removal.
  };

  const handleRemoveImage = (roomIndex, imageIndex) => {
    setRooms(prev => {
      const updated = [...prev];
      const r = { ...updated[roomIndex] };
      const imgs = [...(r.images || [])];
      const [removed] = imgs.splice(imageIndex, 1);
      // URL.revokeObjectURL(removed); // uncomment if you manage File URLs here
      r.images = imgs;
      updated[roomIndex] = r;
      return updated;
    });
  };

  return (
    <Container className="py-4" fluid="md">
      <Stack direction="horizontal" gap={3} className="mb-3">
        <h1 className="m-0">Room Editor CMS</h1>
        
        <Button variant="outline-primary" className="me-3" onClick={() => { navigate('/'); window.location.reload() }}>
          Home
        </Button>
        <Badge bg="primary" className="ms-auto">{rooms.length} rooms</Badge>
        <Button variant="success" onClick={handleAddRoom}>
          + Add Room
        </Button>
      </Stack>

      {rooms.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <Card.Title>No rooms yet</Card.Title>
            <Card.Text>Click “Add Room” to get started.</Card.Text>
            <Button variant="success" onClick={handleAddRoom}>+ Add Room</Button>
          </Card.Body>
        </Card>
      ) : (
        <Tabs
          id="room-tabs"
          activeKey={activeTabIndex}
          onSelect={handleTabSelect}
          className="room-tabs mb-4"
          justify
        >
          {rooms.map((room, index) => (
            <Tab    
              key={room.id}
              eventKey={index}
              title={
                <span>
                  {room.title || 'Untitled'}
                </span>
              }
            >
              <Card className="edit-form shadow-sm">
                <Card.Header className="bg-white">
                  <Stack direction="horizontal" gap={2}>
                  </Stack>
                    <Col md={12}>
                        <Form.Group className="mb-3" controlId={`title-${room.id}`}>
                        <Form.Label className="fw-semibold">Title</Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            value={room.title ?? ''}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. Deluxe Suite"
                        />
                        </Form.Group>
                    </Col>
                </Card.Header>
                <Card.Body>
                  {/* Images */}
                  <Row className="gy-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Room Images</Form.Label>
                        <Row className="mt-3 g-3">
                         <Col xs={12}>
                            <div className="gallery-scroll">
                                {(room.images || []).map((image, imageIndex) => (
                                <div key={imageIndex} className="gallery-item position-relative">
                                    <Card className="h-100">
                                    <Card.Img
                                        src={image.src}
                                        alt={`Image ${imageIndex + 1}`}
                                        className="gallery-img"
                                    />
                                    <CloseButton
                                        aria-label="Remove image"
                                        onClick={() => handleRemoveImage(index, imageIndex)}
                                        className="position-absolute top-0 end-0 m-2 bg-white rounded-circle p-2 shadow-sm"
                                    />
                                    </Card>
                                </div>
                                ))}
                            </div>
                        </Col>
                          <InputGroup className="mb-3">
                            <InputGroup.Text id="basic-addon2">
                              <Button variant="outline" onClick={() => document.getElementById(`image-upload-${room.id}`).click()}>
                                Add Image(s)
                              </Button>
                            </InputGroup.Text>
                            <Form.Control
                              id={`image-upload-${room.id}`}
                              type="file"
                              multiple
                              hidden
                              onChange={(e) => handleAddImage(index, e)}
                            />
                          </InputGroup>
                        </Row>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3" controlId={`title-${room.gemImage}`}>
                      <Form.Label className="fw-semibold">Gem Image</Form.Label>
                      <Form.Control
                          type="text"
                          name="gemImage"
                          value={room.gemImage ?? ''}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="amethyst.jpg"
                      />
                      </Form.Group>
                    </Col>
                    {/* Title & Description */}
                    <Col xs={12}>
                      <Form.Group className="mb-3" controlId={`description-${room.id}`}>
                        <Form.Label className="fw-semibold">Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="description"
                          value={room.description ?? ''}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Describe the room, view, and vibe."
                        />
                      </Form.Group>
                    </Col>

                    {/* Numbers & options */}
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId={`capacity-${room.id}`}>
                        <Form.Label className="fw-semibold">Capacity</Form.Label>
                        <Form.Control
                          type="number"
                          name="capacity"
                          value={room.capacity ?? ''}
                          onChange={(e) => handleChange(index, e)}
                          min={0}
                          placeholder="e.g. 4"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId={`bed-${room.id}`}>
                        <Form.Label className="fw-semibold">Bed</Form.Label>
                        <Form.Control
                          type="text"
                          name="bed"
                          value={room.bed ?? ''}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="e.g. King"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId={`color-${room.id}`}>
                        <Form.Label className="fw-semibold">Accent Color</Form.Label>
                        <Form.Control
                          type="color"
                          name="color"
                          value={toColorInputValue(room.color, '#3b82f6')}
                          onChange={(e) => handleChange(index, e)}
                          title="Choose room color"
                        />
                      </Form.Group>
                    </Col>

                    {/* Facilities */}
                    <Col xs={12}>
                      <div className="d-flex align-items-center justify-content-between">
                        <Form.Label className="fw-semibold mb-0">Facilities</Form.Label>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleAddFacility(index)}
                        >
                          + Add Facility
                        </Button>
                      </div>

                      <Stack gap={2} className="mt-2">
                        <Row xs={1} md={2} className="g-4">
                          {(room.facilities ?? []).map((facility, facilityIndex) => (
                            <Col key={facilityIndex}>
                              <InputGroup>
                                <Form.Control
                                  type="text"
                                  name="facilities"
                                  value={facility ?? ''}
                                  onChange={(e) => handleChange(index, e, facilityIndex)}
                                  placeholder="e.g. Free Wi-Fi"
                                />
                                <Button
                                  variant="outline-danger"
                                  onClick={() => handleRemoveFacility(index, facilityIndex)}
                                  aria-label="Remove facility"
                                >
                                  ✕
                                </Button>
                              </InputGroup>
                            </Col>
                          ))}
                        </Row>
                      </Stack>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>
          ))}
        </Tabs>
      )}
      
      <div className="edit-form">
        <Row className="mt-4 justify-content-center align-items-center">
          <Col xs={6} md={4}>
            <Form.Group className="mb-3" controlId="username">
              <Form.Label>Username</Form.Label>
              <Form.Control type="text" placeholder="Enter username" value={username} onChange={(e) => setUsername(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" placeholder="Enter password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Form.Group>
          </Col>

          <Col className="d-flex justify-content-center">
            <ButtonGroup>
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
              <Button variant="primary" onClick={handleSave}>
                Save
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </div>

    </Container>
  );
};

export default EditPage;
